import type {
  AddMemberInput,
  BalancesDto,
  CreateExpenseInput,
  CreateGroupInput,
  CreatePersonInput,
  CreateSettlementInput,
  ExpenseDto,
  GroupDto,
  GroupSummaryDto,
  MySummaryDto,
  PersonDto,
  PublicGroupDto,
  SettlementDto,
  UpdateGroupInput,
  UpdatePersonInput,
} from "@caloteiros/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { api } from "./api";

/** Chaves de cache em um lugar so: invalidar errado e a fonte de bug em cache. */
export const keys = {
  summary: ["summary"] as const,
  groups: ["groups"] as const,
  group: (groupId: string) => ["group", groupId] as const,
  balances: (groupId: string, mode: string) => ["balances", groupId, mode] as const,
  expenses: (groupId: string) => ["expenses", groupId] as const,
  expense: (groupId: string, expenseId: string) => ["expense", groupId, expenseId] as const,
  settlements: (groupId: string) => ["settlements", groupId] as const,
  people: ["people"] as const,
  publicGroup: (token: string) => ["public", token] as const,
};

export function useSummary(): UseQueryResult<MySummaryDto> {
  return useQuery({ queryKey: keys.summary, queryFn: () => api<MySummaryDto>("/me/summary") });
}

export function useGroups(): UseQueryResult<GroupSummaryDto[]> {
  return useQuery({ queryKey: keys.groups, queryFn: () => api<GroupSummaryDto[]>("/groups") });
}

export function useGroup(groupId: string): UseQueryResult<GroupDto> {
  return useQuery({
    queryKey: keys.group(groupId),
    queryFn: () => api<GroupDto>(`/groups/${groupId}`),
    enabled: Boolean(groupId),
  });
}

export function useBalances(
  groupId: string,
  mode: "pairwise" | "simplified",
): UseQueryResult<BalancesDto> {
  return useQuery({
    queryKey: keys.balances(groupId, mode),
    queryFn: () => api<BalancesDto>(`/groups/${groupId}/balances?mode=${mode}`),
    enabled: Boolean(groupId),
  });
}

export function useExpenses(groupId: string): UseQueryResult<ExpenseDto[]> {
  return useQuery({
    queryKey: keys.expenses(groupId),
    queryFn: () => api<ExpenseDto[]>(`/groups/${groupId}/expenses`),
    enabled: Boolean(groupId),
  });
}

export function useExpense(groupId: string, expenseId: string | undefined) {
  return useQuery({
    queryKey: keys.expense(groupId, expenseId ?? ""),
    queryFn: () => api<ExpenseDto>(`/groups/${groupId}/expenses/${expenseId}`),
    enabled: Boolean(groupId && expenseId),
  });
}

export function useSettlements(groupId: string): UseQueryResult<SettlementDto[]> {
  return useQuery({
    queryKey: keys.settlements(groupId),
    queryFn: () => api<SettlementDto[]>(`/groups/${groupId}/settlements`),
    enabled: Boolean(groupId),
  });
}

export function usePeople(): UseQueryResult<PersonDto[]> {
  return useQuery({ queryKey: keys.people, queryFn: () => api<PersonDto[]>("/people") });
}

export function usePublicGroup(token: string): UseQueryResult<PublicGroupDto> {
  return useQuery({
    queryKey: keys.publicGroup(token),
    queryFn: () => api<PublicGroupDto>(`/public/groups/${token}`, { anonymous: true }),
    enabled: Boolean(token),
    retry: false,
  });
}

/**
 * Depois de mexer em qualquer coisa de um grupo, tudo que depende dele volta
 * a ser buscado: saldos, despesas, acertos, o resumo da home e a lista.
 */
function useInvalidateGroup(groupId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.group(groupId) }),
      queryClient.invalidateQueries({ queryKey: ["balances", groupId] }),
      queryClient.invalidateQueries({ queryKey: keys.expenses(groupId) }),
      queryClient.invalidateQueries({ queryKey: keys.settlements(groupId) }),
      queryClient.invalidateQueries({ queryKey: keys.groups }),
      queryClient.invalidateQueries({ queryKey: keys.summary }),
    ]);
  };
}

export function useCreateGroup(): UseMutationResult<GroupDto, Error, CreateGroupInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGroupInput) =>
      api<GroupDto>("/groups", { method: "POST", body: input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.groups });
      await queryClient.invalidateQueries({ queryKey: keys.summary });
    },
  });
}

export function useUpdateGroup(groupId: string): UseMutationResult<GroupDto, Error, UpdateGroupInput> {
  const invalidate = useInvalidateGroup(groupId);
  return useMutation({
    mutationFn: (input: UpdateGroupInput) =>
      api<GroupDto>(`/groups/${groupId}`, { method: "PATCH", body: input }),
    onSuccess: invalidate,
  });
}

export function useDeleteGroup(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => api<void>(`/groups/${groupId}`, { method: "DELETE" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.groups });
      await queryClient.invalidateQueries({ queryKey: keys.summary });
    },
  });
}

export function useAddMember(groupId: string): UseMutationResult<GroupDto, Error, AddMemberInput> {
  const invalidate = useInvalidateGroup(groupId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddMemberInput) =>
      api<GroupDto>(`/groups/${groupId}/members`, { method: "POST", body: input }),
    onSuccess: async () => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: keys.people });
    },
  });
}

export function useRemoveMember(groupId: string): UseMutationResult<GroupDto, Error, string> {
  const invalidate = useInvalidateGroup(groupId);
  return useMutation({
    mutationFn: (personId: string) =>
      api<GroupDto>(`/groups/${groupId}/members/${personId}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

export function useSetShare(
  groupId: string,
): UseMutationResult<GroupDto, Error, { enabled: boolean; rotate?: boolean }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { enabled: boolean; rotate?: boolean }) =>
      api<GroupDto>(`/groups/${groupId}/share`, { method: "POST", body: input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.group(groupId) });
    },
  });
}

export function useCreateExpense(
  groupId: string,
): UseMutationResult<ExpenseDto, Error, CreateExpenseInput> {
  const invalidate = useInvalidateGroup(groupId);
  return useMutation({
    mutationFn: (input: CreateExpenseInput) =>
      api<ExpenseDto>(`/groups/${groupId}/expenses`, { method: "POST", body: input }),
    onSuccess: invalidate,
  });
}

export function useUpdateExpense(
  groupId: string,
  expenseId: string,
): UseMutationResult<ExpenseDto, Error, CreateExpenseInput> {
  const invalidate = useInvalidateGroup(groupId);
  return useMutation({
    mutationFn: (input: CreateExpenseInput) =>
      api<ExpenseDto>(`/groups/${groupId}/expenses/${expenseId}`, { method: "PUT", body: input }),
    onSuccess: invalidate,
  });
}

export function useDeleteExpense(groupId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateGroup(groupId);
  return useMutation({
    mutationFn: (expenseId: string) =>
      api<void>(`/groups/${groupId}/expenses/${expenseId}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

export function useCreateSettlement(
  groupId: string,
): UseMutationResult<SettlementDto, Error, CreateSettlementInput> {
  const invalidate = useInvalidateGroup(groupId);
  return useMutation({
    mutationFn: (input: CreateSettlementInput) =>
      api<SettlementDto>(`/groups/${groupId}/settlements`, { method: "POST", body: input }),
    onSuccess: invalidate,
  });
}

export function useDeleteSettlement(groupId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateGroup(groupId);
  return useMutation({
    mutationFn: (settlementId: string) =>
      api<void>(`/groups/${groupId}/settlements/${settlementId}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

export function useSettleAll(
  groupId: string,
): UseMutationResult<SettlementDto[], Error, "pairwise" | "simplified"> {
  const invalidate = useInvalidateGroup(groupId);
  return useMutation({
    mutationFn: (mode: "pairwise" | "simplified") =>
      api<SettlementDto[]>(`/groups/${groupId}/settle-all`, { method: "POST", body: { mode } }),
    onSuccess: invalidate,
  });
}

export function useCreatePerson(): UseMutationResult<PersonDto, Error, CreatePersonInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePersonInput) =>
      api<PersonDto>("/people", { method: "POST", body: input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.people });
    },
  });
}

export function useUpdatePerson(): UseMutationResult<
  PersonDto,
  Error,
  { personId: string; input: UpdatePersonInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ personId, input }: { personId: string; input: UpdatePersonInput }) =>
      api<PersonDto>(`/people/${personId}`, { method: "PATCH", body: input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });
}

export function useLinkPerson(): UseMutationResult<
  PersonDto,
  Error,
  { personId: string; email: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ personId, email }: { personId: string; email: string }) =>
      api<PersonDto>(`/people/${personId}/link`, { method: "POST", body: { email } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });
}

export function useUnlinkPerson(): UseMutationResult<PersonDto, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (personId: string) =>
      api<PersonDto>(`/people/${personId}/link`, { method: "DELETE" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });
}

export function useMergePeople(): UseMutationResult<
  PersonDto,
  Error,
  { sourceId: string; targetId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { sourceId: string; targetId: string }) =>
      api<PersonDto>("/people/merge", { method: "POST", body: input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });
}

export function useArchivePerson(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (personId: string) => api<void>(`/people/${personId}`, { method: "DELETE" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });
}
